import React, { useState, useEffect, useCallback } from 'react';
import './SociosCredencialesList.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';

function SociosCredencialesList({ token }) {
    const CREDENTIALS_CACHE_KEY = 'abis_credentials_cache_v1';
    const [socios, setSocios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generandoCredencial, setGenerandoCredencial] = useState(null);
    const [consultandoCredencial, setConsultandoCredencial] = useState(null);
    const [modalQR, setModalQR] = useState(null);
    const [credencialesCache, setCredencialesCache] = useState(() => {
        try {
            const raw = localStorage.getItem(CREDENTIALS_CACHE_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    });

    // Estados para búsqueda y paginado
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);

    const fetchSocios = useCallback(async () => {
        try {
            setLoading(true);

            // Construir URL con parámetros de búsqueda y paginado
            const params = new URLSearchParams({
                page: currentPage,
                page_size: pageSize
            });

            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }

            const response = await apiFetch(`http://localhost:8000/api/socios/?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (!response.ok) {
                console.error('Error en la respuesta:', data);
                alert('Error al cargar socios: ' + (data.detail || JSON.stringify(data)));
                setSocios([]);
                return;
            }

            // Actualizar estados con respuesta paginada
            setSocios(data.results || []);
            setTotalCount(data.count || 0);
            setNextPage(data.next);
            setPrevPage(data.previous);
        } catch (err) {
            console.error('Error al cargar socios:', err);
            alert('Error al cargar la lista de socios: ' + err.message);
            setSocios([]);
        } finally {
            setLoading(false);
        }
    }, [token, searchTerm, currentPage, pageSize]);

    useEffect(() => {
        fetchSocios();
    }, [fetchSocios]);

    useEffect(() => {
        try {
            localStorage.setItem(CREDENTIALS_CACHE_KEY, JSON.stringify(credencialesCache));
        } catch {
            // Ignorar errores de almacenamiento local
        }
    }, [credencialesCache]);

    const cacheCredencial = (socioId, payload) => {
        setCredencialesCache((prev) => ({
            ...prev,
            [socioId]: {
                ...payload,
                checked_at: new Date().toISOString()
            }
        }));
    };

    const findExistingCredencial = async (socioId) => {
        const response = await apiFetch(`http://localhost:8000/api/socios/${socioId}/credencial/`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.detail || 'No se pudo consultar la credencial');
        }

        if (!data.exists || !data.pdf_url) {
            return null;
        }

        return {
            pdf_url: data.pdf_url,
            qr_url: data.qr_url || null
        };
    };

    const handleVerCredencial = async (socioId, nombreCompleto) => {
        const cache = credencialesCache[socioId];
        if (cache?.pdf_url) {
            setModalQR({
                socio: nombreCompleto,
                qr_url: cache.qr_url,
                pdf_url: cache.pdf_url
            });
            return;
        }

        try {
            setConsultandoCredencial(socioId);
            const existente = await findExistingCredencial(socioId);

            if (existente?.pdf_url) {
                cacheCredencial(socioId, existente);
                setModalQR({
                    socio: nombreCompleto,
                    qr_url: existente.qr_url,
                    pdf_url: existente.pdf_url
                });
            } else {
                alert('No se encontró una credencial existente. Puedes generarla.');
            }
        } catch (err) {
            alert('No se pudo verificar credencial existente: ' + err.message);
        } finally {
            setConsultandoCredencial(null);
        }
    };

    const handleGenerarCredencial = async (socioId, nombreCompleto) => {
        if (!window.confirm(`¿Generar credencial para ${nombreCompleto}?`)) {
            return;
        }

        try {
            setGenerandoCredencial(socioId);
            const response = await apiFetch(`http://localhost:8000/api/socios/${socioId}/generar-credencial/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok) {
                // Mostrar modal con QR y opciones
                const credencial = {
                    qr_url: data.qr_url,
                    pdf_url: data.pdf_url
                };
                cacheCredencial(socioId, credencial);
                setModalQR({
                    socio: nombreCompleto,
                    qr_url: credencial.qr_url,
                    pdf_url: credencial.pdf_url,
                    qr_filename: data.qr_filename
                });
            } else {
                alert('Error: ' + (data.detail || JSON.stringify(data)));
            }
        } catch (err) {
            alert('Error al generar credencial: ' + err.message);
        } finally {
            setGenerandoCredencial(null);
        }
    };

    const cerrarModal = () => {
        setModalQR(null);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reiniciar a página 1 cuando se busca
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setCurrentPage(1); // Reiniciar a página 1 cuando se cambia el tamaño
    };

    const handlePreviousPage = () => {
        if (prevPage && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (nextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div>
            <h3>Gestión de Credenciales de Socios</h3>
            <p className="intro-text">
                En esta sección puedes generar las credenciales para los socios registrados.
            </p>

            {/* Buscador y controles */}
            <div className="controls-row">
                {/* Buscador */}
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, apellido o email..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-input"
                    />
                </div>

                {/* Selector de resultados por página */}
                <div className="page-controls">
                    <label htmlFor="pageSize" className="label-inline">Mostrar:</label>
                    <select
                        id="pageSize"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="select-page"
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                    <span style={{ fontSize: '14px', color: '#666' }}>por página</span>
                </div>

                {/* Contador de resultados */}
                <div className="total-count">
                    Total: <strong>{totalCount}</strong> socio{totalCount !== 1 ? 's' : ''}
                    {searchTerm && ` encontrado${totalCount !== 1 ? 's' : ''}`}
                </div>
            </div>

            {loading && <p>Cargando socios...</p>}

            {!loading && socios.length === 0 && (
                <p>
                    {searchTerm
                        ? `No se encontraron socios con "${searchTerm}"`
                        : 'No hay socios registrados en el sistema.'
                    }
                </p>
            )}

            {!loading && Array.isArray(socios) && socios.length > 0 && (
                <table className="table-full cred-table">
                    <thead>
                        <tr className="table-head">
                            <th className="th">Reg. ABIS</th>
                            <th className="th">Nombre Completo</th>
                            <th className="th">C.I.</th>
                            <th className="th">RNI</th>
                            <th className="th">Email</th>
                            <th className="th">Fecha de Inscripción</th>
                            <th className="th th-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {socios.map(socio => {
                            const nombreCompleto = `${socio.nombres || ''} ${socio.apellidos || ''}`.trim();
                            const ci = socio.ci || 'N/A';
                            const complemento = socio.complemento ? `-${socio.complemento}` : '';
                            const expedido = socio.expedido ? ` ${socio.expedido}` : '';
                            const ciCompleto = `${ci}${complemento}${expedido}`;

                            return (
                                <tr key={socio.id}>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                        {socio.reg_abis || 'N/A'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                        {nombreCompleto || 'Sin nombre'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                        {ciCompleto}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                        {socio.nreg_profesional || 'N/A'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                        {socio.email || 'N/A'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                        {socio.fecha_ins ? new Date(socio.fecha_ins).toLocaleDateString('es-ES') : 'N/A'}
                                    </td>
                                    <td className="td td-center">
                                        <DashboardButton
                                            variant="view"
                                            size="sm"
                                            onClick={() => handleVerCredencial(socio.id, nombreCompleto)}
                                            disabled={consultandoCredencial === socio.id}
                                        >
                                            {consultandoCredencial === socio.id ? 'Consultando...' : 'Ver Credencial'}
                                        </DashboardButton>
                                        <DashboardButton
                                            onClick={() => handleGenerarCredencial(socio.id, nombreCompleto)}
                                            disabled={generandoCredencial === socio.id}
                                            variant="success"
                                            size="sm"
                                        >
                                            {generandoCredencial === socio.id ? 'Generando...' : 'Generar Credencial'}
                                        </DashboardButton>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {/* Controles de paginación */}
            {!loading && socios.length > 0 && (
                <div className="pagination-controls">
                    {/* Botón Anterior */}
                    <DashboardButton onClick={handlePreviousPage} disabled={!prevPage || currentPage === 1} variant="view" className="pagination-btn">
                        ← Anterior
                    </DashboardButton>

                    {/* Información de página */}
                    <div className="page-info">
                        Página <strong>{currentPage}</strong> de <strong>{totalPages || 1}</strong>
                        {' '}
                        ({socios.length} de {totalCount} socios)
                    </div>

                    {/* Botón Siguiente */}
                    <DashboardButton onClick={handleNextPage} disabled={!nextPage} variant="view" className="pagination-btn">
                        Siguiente →
                    </DashboardButton>
                </div>
            )}

            {/* Modal para mostrar QR y PDF */}
            {modalQR && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Credencial Generada</h3>
                        <p><strong>Socio:</strong> {modalQR.socio}</p>

                        <div className="modal-qr-wrap">
                            <img src={modalQR.qr_url} alt="Código QR" className="modal-qr" />
                        </div>

                        <div className="modal-actions">
                            {modalQR.qr_url && (
                                <a href={modalQR.qr_url} download className="btn view">Descargar QR</a>
                            )}
                            <a href={modalQR.pdf_url} target="_blank" rel="noopener noreferrer" className="btn download">Ver PDF</a>
                        </div>

                        <DashboardButton onClick={cerrarModal} variant="secondary" className="modal-close">Cerrar</DashboardButton>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SociosCredencialesList;
